<template>
  <div class="os-board" :class="{ 'os-angosto': apilado }" ref="board">
    <!-- La pantalla de reportes no pinta su renglón de título para este reporte: el título y la estrella van aquí. -->
    <div class="os-title-row">
      <div class="os-title">{{ T.titulo }}</div>
      <v-btn class="os-fav" text small :color="esFavorito ? 'yellow' : 'grey'" :title="esFavorito ? T.favQuitar : T.favAgregar"
        @click="$emit('alternar-favorito')"><v-icon>mdi-star</v-icon></v-btn>
    </div>
    <div class="os-sub" v-if="subtitulo">{{ subtitulo }}</div>

    <!-- Filtros -->
    <div class="os-toolbar">
      <div class="os-filtros">
        <label class="os-input os-texto"><span class="lbl">{{ T.buscarEtiqueta }}</span>
          <input type="text" v-model="texto" :placeholder="T.buscarEjemplo">
        </label>
        <label class="os-input os-sel os-sel--semana"><span class="k">{{ T.semana }}</span>
          <select v-model="filtros.referencia" :disabled="cargandoAlcance" @change="buscar()">
            <option v-for="s in semanas" :key="s.inicio" :value="s.inicio">{{ s.texto }}</option>
          </select>
        </label>
        <label class="os-input os-sel"><span class="k">{{ T.alcance }}</span>
          <select v-model="filtros.idArea" :disabled="cargandoAlcance" @change="buscar()">
            <option :value="null">{{ T.alcancePlanta }}</option>
            <option v-for="a in areas" :key="'a' + a.idArea" :value="a.idArea">{{ a.nombre }}</option>
          </select>
        </label>
        <label class="os-input os-switch" :title="T.todasAyuda">
          <input type="checkbox" v-model="todas" @change="alCambiarTodas()"> {{ T.todas }}
        </label>
        <button class="os-btn os-btn-primary" :title="T.buscarAyuda" @click="buscar()" :disabled="cargandoAlcance || sinAlcance">
          <v-icon small color="white">mdi-magnify</v-icon> {{ T.buscar }}
        </button>
      </div>
      <div class="os-actions">
        <button class="os-btn os-btn-outline" @click="pantallaCompleta()">
          <v-icon small color="#2563d6">mdi-fullscreen</v-icon> {{ T.pantallaCompleta }}
        </button>
      </div>
    </div>

    <v-progress-linear v-if="loading || cargandoAlcance" indeterminate color="#2563d6" class="mt-3" />

    <!-- Mensajes de estado. Nunca se pinta el texto que mande el API. -->
    <div class="os-aviso os-aviso--error" v-if="mensajeError">
      <span>{{ mensajeError }}</span>
      <button class="os-reintentar" v-if="puedeReintentar" @click="reintentar()">{{ T.reintentar }}</button>
    </div>
    <div class="os-aviso" v-if="respuesta && sinHorario">{{ T.sinHorario }}</div>
    <div class="os-aviso" v-if="respuesta && !renglones.length">{{ T.sinMaquinas }}</div>
    <div class="os-aviso" v-if="respuesta && resumen.sinEstandar > 0">{{ textoSinEstandar }}</div>
    <!-- Las dos razones por las que una orden se queda sin esperadas son distintas y pueden darse a la vez, así que cada una
         lleva su renglón: sin ellos el total pinta un porcentaje que no sale de dividir las producidas entre las esperadas.
         Sin horario no hay tiempo transcurrido que repartir, así que TODAS se quedan sin esperadas sin que medie un solo paro
         programado: ahí este renglón diría una causa falsa, y el aviso de arriba ya explica por qué nada tiene esperadas. -->
    <div class="os-aviso" v-if="respuesta && !sinHorario && resumen.sinTiempoEsperado > 0">{{ textoSinTiempoEsperado }}</div>
    <div class="os-aviso" v-if="respuesta && topeDeOrdenes">{{ T.topeDeOrdenes }}</div>

    <template v-if="respuesta && renglones.length">
      <!-- Tarjetas -->
      <div class="os-toprow">
        <div class="os-topcard os-topcard--click" :class="{ 'is-on': soloBajaron }" role="button" tabindex="0"
          :title="T.tarjetaAyuda" @click="alternarBajaron()" @keydown.enter.prevent="alternarBajaron()"
          @keydown.space.prevent="alternarBajaron()">
          <div class="os-sec" style="flex:1 1 auto; min-width:0">
            <div class="os-sec-head">
              <div class="os-sec-title">{{ T.tarjetaTitulo }}</div>
              <span class="os-card-hint">{{ soloBajaron ? T.verTodas : T.verBajaron }}</span>
            </div>
            <div class="os-sil">
              <div class="os-s3" v-for="b in queMasBajaron" :key="'b' + b.id">
                <span class="d">▼ {{ pts(-b.cambio) }}<small>pts</small></span>
                <span class="n">
                  <span class="nom">{{ b.maquina }}</span>
                  <span class="sub">{{ pctf(b.anterior) }} → {{ pctf(b.actual) }}</span>
                </span>
              </div>
              <span class="os-s3" v-if="!queMasBajaron.length"><span class="sub">{{ T.ningunaBajo }}</span></span>
            </div>
          </div>
        </div>
        <div class="os-topcard os-topcard--kpi">
          <div class="os-sec">
            <div class="os-sec-title">{{ T.kpiProducidas }}</div>
            <div class="os-row-inline">
              <span class="os-big">{{ fmt(resumen.producidas, 0) }}</span>
              <span class="os-pct" :title="T.kpiProducidasAyuda">{{ pctf(resumen.cumplimiento) }}</span>
            </div>
          </div>
          <div class="os-sec">
            <div class="os-sec-title os-sec-title--normal">{{ T.kpiParo }}</div>
            <div class="os-row-inline">
              <span class="os-big">{{ hrs(resumen.segundosParo) }}<small>h</small></span>
              <span class="os-pct" :title="T.kpiParoAyuda">{{ pctf(resumen.porcentajeParo) }}</span>
            </div>
          </div>
          <div class="os-sec">
            <div class="os-sec-title os-sec-title--normal">{{ T.kpiScrap }}</div>
            <span class="os-big">{{ fmt(resumen.scrap, 0) }}<small>{{ T.pzas }}</small></span>
          </div>
          <div class="os-sec">
            <div class="os-sec-title os-sec-title--normal">{{ T.kpiOEE }}</div>
            <span class="os-big">{{ pctf(resumen.oee) }}</span>
          </div>
          <div class="os-sec">
            <div class="os-sec-title os-sec-title--normal">{{ T.kpiMaquinas }}</div>
            <span class="os-big">{{ resumen.maquinasTrabajando }}<small>{{ T.de }} {{ resumen.maquinasTotales }}</small></span>
          </div>
        </div>
      </div>

      <div class="os-legend">
        <span class="os-lg" v-if="enCurso"><i class="sw-bar"></i> {{ T.leyendaCon }}</span>
        <span class="os-lg" v-if="enCurso"><i class="sw-soft"></i> {{ T.leyendaSin }}</span>
        <span class="os-lg os-lg--fuera" v-if="fueraDelReporte.length"><b>{{ T.fuera }}</b> {{ fueraDelReporte.join(' · ') }}</span>
      </div>

      <section class="os-blk">
        <div class="os-blk-head">
          <div class="os-seg" role="group" :aria-label="T.ordenAria">
            <button :aria-pressed="String(orden == 'cumplimiento')" :title="T.ordenCumplimientoAyuda" @click="orden = 'cumplimiento'">{{ T.ordenCumplimiento }}</button>
            <button :aria-pressed="String(orden == 'cambio')" :title="T.ordenCambioAyuda" @click="orden = 'cambio'">{{ T.ordenCambio }}</button>
          </div>
          <span class="os-hint">{{ T.hint }}</span>
          <div class="os-seg os-seg--r" role="group" :aria-label="T.medidaAria">
            <button :aria-pressed="String(medida == 'cumplimiento')" @click="medida = 'cumplimiento'">{{ T.medidaCumplimiento }}</button>
            <button :aria-pressed="String(medida == 'piezas')" @click="medida = 'piezas'">{{ T.medidaPiezas }}</button>
          </div>
        </div>
        <div class="os-blk-body" :class="{ 'os-apilado': apilado }">
          <div class="os-tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th class="l" v-if="todas">{{ T.colArticulo }}</th>
                  <th class="l">{{ T.colMaquina }}</th>
                  <th>{{ T.colCumplimiento }}</th>
                  <th>{{ T.colCambio1 }}<br>{{ T.colCambio2 }}</th>
                  <th>{{ T.colProducidas1 }}<br>{{ T.colProducidas2 }}</th>
                  <th>{{ T.colEsperadas1 }}<br>{{ enCurso ? T.colEsperadas2 : T.colEsperadas2Cerrada }}</th>
                  <th>{{ T.colProgramados1 }}<br>{{ T.colProgramados2 }}</th>
                  <th>{{ T.colParo1 }}<br>{{ T.colParo2 }}</th>
                  <th>{{ T.colScrap1 }}<br>{{ T.colScrap2 }}</th>
                  <th>{{ T.colOEE1 }}<br>{{ T.colOEE2 }}</th>
                  <template v-if="todas">
                    <th>{{ T.colDisponibilidad }}</th>
                    <th>{{ T.colRendimiento }}</th>
                    <th>{{ T.colCalidad }}</th>
                    <th>{{ T.colParos }}</th>
                    <th>{{ T.colEstandar }}</th>
                    <th>{{ T.colEsperadasSemana }}</th>
                    <th>{{ T.colFuera }}</th>
                    <th>{{ T.colAcumulado }}</th>
                  </template>
                  <th class="l" v-if="todas">{{ T.colUltima }}</th>
                  <th class="l" v-if="todas">{{ T.colProduccion }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in visibles" :key="'r' + o.id" tabindex="0"
                  :class="{ 'is-sel': sel === o.id, 'is-hot': hot === o.id }" @mouseenter="hot = o.id"
                  @mouseleave="hot = null" @click="alternarDetalle(o.id)" @keydown.enter.prevent="alternarDetalle(o.id)"
                  @keydown.space.prevent="alternarDetalle(o.id)">
                  <td class="l" v-if="todas">{{ o.articulo }}</td>
                  <td class="l ot" :title="o.clave">{{ o.maquina }}<span class="mut" v-if="todas && o.area"> · {{ o.area }}</span>
                    <span class="os-chip os-chip--warn" v-if="o.arrancoEnLaSemana" :title="tituloArranco(o)">{{ T.chipArranco }}</span>
                  </td>
                  <td>
                    <span v-if="o.cumplimiento == null" class="mut" :title="tituloSinValor(o)">{{ o.sinEstandar ? T.sinEstandar : '—' }}</span>
                    <span v-else class="os-av"><span class="b"><i :style="{ width: anchoBarra(o.cumplimiento) }"></i></span>{{ pctf(o.cumplimiento) }}</span>
                  </td>
                  <td :title="tituloCambio(o)" :class="o.cambio == null ? 'mut' : (o.cambio < 0 ? 'baja' : 'sube')">{{ textoCambio(o.cambio) }}</td>
                  <td>{{ fmt(o.producidas, 0) }}</td>
                  <td class="mut">{{ o.esperadas == null ? '—' : fmt(o.esperadas, 0) }}</td>
                  <td class="mut">{{ hrs(o.segundosProgramados) }} h</td>
                  <td>{{ hrs(o.segundosParo) }} h <span class="mut">{{ pctf(o.porcentajeParo) }}</span></td>
                  <td>{{ fmt(o.scrap, 0) }}</td>
                  <td :title="o.oee == null ? tituloSinValor(o) : null"><b>{{ pctf(o.oee) }}</b></td>
                  <template v-if="todas">
                    <td class="mut">{{ pctf(o.disponibilidad) }}</td>
                    <td class="mut">{{ pctf(o.rendimiento) }}</td>
                    <td class="mut">{{ pctf(o.calidad) }}</td>
                    <td class="mut">{{ fmt(o.numeroParos, 0) }}</td>
                    <td class="mut">{{ o.sinEstandar ? '—' : fmt(o.estandar, 0) + ' s' }}</td>
                    <td class="mut">{{ o.esperadasSemana == null ? '—' : fmt(o.esperadasSemana, 0) }}</td>
                    <td class="mut">{{ fmt(o.fuera, 0) }}</td>
                    <td class="mut">{{ textoAcumulado(o.id) }}</td>
                  </template>
                  <td class="l" v-if="todas">
                    <span class="os-chip os-chip--warn" v-if="o.sinPiezas24">⚠ {{ fechaCorta(o.ultima) }}</span>
                    <template v-else>{{ fechaCorta(o.ultima) }}</template>
                    <span class="mut"> {{ hace(o.ultima) }}</span>
                  </td>
                  <td class="l" v-if="todas"><span class="os-chip" :class="o.estado == 1 ? 'os-chip--ok' : 'os-chip--warn'">● {{ textoEstado(o.estado) }}</span></td>
                </tr>
                <tr v-if="!visibles.length" class="vacio"><td :colspan="todas ? 20 : 9">{{ T.sinCoincidencias }}</td></tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="l" :colspan="todas ? 2 : 1">{{ textoTotal }}</td>
                  <td>{{ pctf(resumen.cumplimiento) }}</td>
                  <td :class="resumen.cambio == null ? '' : (resumen.cambio < 0 ? 'baja' : 'sube')">{{ textoCambio(resumen.cambio) }}</td>
                  <td>{{ fmt(resumen.producidas, 0) }}</td>
                  <td>{{ fmt(resumen.esperadas, 0) }}</td>
                  <td>{{ hrs(resumen.segundosProgramados) }} h</td>
                  <td>{{ hrs(resumen.segundosParo) }} h <span class="norm">{{ pctf(resumen.porcentajeParo) }}</span></td>
                  <td>{{ fmt(resumen.scrap, 0) }}</td>
                  <td>{{ pctf(resumen.oee) }}</td>
                  <template v-if="todas">
                    <td></td><td></td><td></td>
                    <td>{{ fmt(resumen.numeroParos, 0) }}</td>
                    <td></td>
                    <td>{{ fmt(resumen.esperadasSemana, 0) }}</td>
                    <td>{{ fmt(resumen.fuera, 0) }}</td>
                    <td></td>
                  </template>
                  <td colspan="2" v-if="todas"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="os-chart-wrap" ref="grafica">
            <svg :width="anchoSvg" :height="altoGrafica" :viewBox="`0 0 ${anchoSvg} ${altoGrafica}`" role="img" :aria-label="medida == 'cumplimiento' ? T.ariaCumplimiento : T.ariaPiezas">
              <g v-for="i in [0, 1, 2, 3, 4]" :key="'tk' + i">
                <line class="ax" :x1="gx(i / 4)" :x2="gx(i / 4)" :y1="G.t" :y2="altoGrafica - G.b" />
                <text class="ax-lbl" :x="gx(i / 4)" :y="altoGrafica - 6" text-anchor="middle">{{ etiquetaEje(i / 4) }}</text>
              </g>
              <g v-for="(o, i) in visibles" :key="'g' + o.id">
                <text class="y-lbl" :x="G.l - 8" :y="gy(i) + 10" text-anchor="end">{{ o.maquina }}</text>
                <rect class="bar-rect" :class="{ soft: o.sinPiezas24, 'is-hot': hot === o.id }" :x="G.l" :y="gy(i)"
                  :width="anchoGrafica(o)" :height="G.rh - 9" rx="3" />
                <text class="val-lbl" :x="G.l + anchoGrafica(o) + 5" :y="gy(i) + 10">{{ medida == 'cumplimiento' ? pctf(o.cumplimiento) : fmt(o.producidas, 0) }}</text>
                <rect class="hit" x="0" :y="gy(i) - 4" :width="anchoSvg" :height="G.rh" @mouseenter="entraBarra(o, $event)"
                  @mousemove="mueveTip($event)" @mouseleave="saleBarra()" @click="clicBarra(o)" />
              </g>
            </svg>
          </div>
        </div>
        <div class="os-blk-foot">
          <p v-for="(p, i) in ayuda" :key="'ay' + i">{{ p }}</p>
        </div>
      </section>

      <!-- Detalle de la orden elegida -->
      <section class="os-det" ref="detalle" v-if="seleccionada">
        <div class="os-det-head">
          <b>{{ seleccionada.clave }}</b>
          <span class="mut">{{ [seleccionada.articulo, seleccionada.maquina, seleccionada.area].filter(a => a).join(' · ') }}</span>
          <span class="os-card-hint os-cerrar" role="button" tabindex="0" @click="sel = null" @keydown.enter.prevent="sel = null">{{ T.cerrar }}</span>
        </div>
        <div class="os-det-grid">
          <div class="kv"><span>{{ T.detProducidas }}</span><b>{{ fmt(seleccionada.producidas, 0) }}</b></div>
          <div class="kv"><span>{{ enCurso ? T.detEsperadas : T.detEsperadasCerrada }}</span><b>{{ seleccionada.esperadas == null ? T.sinEstandar : fmt(seleccionada.esperadas, 0) }}</b></div>
          <div class="kv"><span>{{ T.detCumplimiento }}</span><b>{{ pctf(seleccionada.cumplimiento) }}</b></div>
          <div class="kv"><span>{{ textoSemanaPasada }}</span><b>{{ detalleSemanaPasada }}</b></div>
          <div class="kv"><span>{{ T.detProgramados }}</span><b>{{ detalleProgramados }}</b></div>
          <div class="kv"><span>{{ T.detParo }}</span><b>{{ detalleParo }}</b></div>
          <div class="kv"><span>{{ T.detScrap }}</span><b>{{ fmt(seleccionada.scrap, 0) }} {{ T.piezas }}</b></div>
          <div class="kv"><span>{{ T.detOEE }}</span><b>{{ detalleOEE }}</b></div>
          <div class="kv"><span :title="T.cicloAyuda">{{ T.detEstandarCiclo }}</span><b :title="T.cicloAyuda">{{ detalleCiclo }}</b></div>
          <div class="kv"><span>{{ T.detEsperadasSemana }}</span><b>{{ seleccionada.esperadasSemana == null ? '—' : fmt(seleccionada.esperadasSemana, 0) }}</b></div>
          <div class="kv"><span>{{ T.detAcumulado }}</span><b>{{ textoAcumulado(seleccionada.id) }}</b></div>
          <div class="kv"><span>{{ T.detInicioReal }}</span><b>{{ textoInicioReal(seleccionada.id) }}</b></div>
          <div class="kv"><span>{{ T.detUltima }}</span><b>{{ fechaCorta(seleccionada.ultima) }} · {{ hace(seleccionada.ultima) }}</b></div>
        </div>
        <div class="os-det-nota" v-if="seleccionada.arrancoEnLaSemana">{{ tituloArranco(seleccionada) }}</div>
        <div class="os-det-nota">{{ T.cicloAyuda }}</div>
      </section>
    </template>

    <!-- Tooltip propio: vive dentro del tablero para que también se vea en pantalla completa -->
    <div class="os-tip" v-if="tip.visible" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
      <b>{{ tip.titulo }}</b><br>
      {{ tip.linea1 }}<br>
      {{ tip.linea2 }}<br>
      <span class="m">{{ tip.linea3 }}</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

// ===== Decisiones de presentación fáciles de cambiar (un solo lugar) =====
// La semana en curso se refresca sola cada este lapso, y sólo si el reporte está activo, a la vista y sin otra consulta en vuelo.
const REFRESCO_MS = 60000;
// Por debajo de este ancho del tablero (no de la ventana: dentro de la plataforma el menú lateral quita ancho) la gráfica baja debajo de la tabla.
const ANCHO_MINIMO_LADO_A_LADO = 1000;
// Ancho mínimo con el que se dibuja la gráfica, en pixeles.
const ANCHO_MINIMO_GRAFICA = 240;
// Órdenes por llamada al pedir el acumulado (el API rechaza más de 50).
const ORDENES_POR_LLAMADA_ACUMULADO = 50;

// ===== Textos: todos aquí, en español =====
const TEXTOS = {
  titulo: 'Órdenes de trabajo en progreso · avance semanal',
  favAgregar: 'Agregar a favoritos', favQuitar: 'Quitar de favoritos',
  subEnCurso: 'Semana de trabajo del {a} al {b}, en curso: van {h} de {H} h laborables al {corte} · {alcance}',
  subCerrada: 'Semana de trabajo del {a} al {b} · {H} h laborables · {alcance}',
  subSinHorario: 'Semana del {a} al {b} · {alcance}',
  buscarEtiqueta: 'Buscar orden o artículo', buscarEjemplo: 'Orden, artículo o máquina…',
  semana: 'Semana:', semanaEnCurso: '· en curso', alcance: 'Alcance:', alcancePlanta: 'Toda la planta',
  todas: 'Todas las columnas',
  todasAyuda: 'Artículo, disponibilidad, rendimiento, calidad, número de paros, tiempo estándar, esperadas de la semana completa, piezas fuera de horario, acumulado de la orden, última pieza y estado de producción',
  buscar: 'Buscar', buscarAyuda: 'Volver a consultar con la semana y el alcance elegidos', pantallaCompleta: 'Pantalla completa',
  tarjetaTitulo: 'Las 3 máquinas que más bajaron contra la semana pasada',
  tarjetaAyuda: 'Ver sólo las máquinas que bajaron contra la semana pasada',
  verBajaron: 'Ver las que bajaron ›', verTodas: 'Ver todas ›',
  ningunaBajo: 'Ninguna máquina bajó contra la semana pasada.',
  kpiProducidas: 'Producidas en la semana', kpiProducidasAyuda: 'Producidas en la semana entre esperadas, de las máquinas que están trabajando. El porcentaje sólo toma las órdenes que tienen esperadas, así que puede no coincidir con dividir los dos totales.',
  kpiParo: 'Tiempo de paro', kpiParoAyuda: 'Horas de paro dentro del horario de planta entre el tiempo en que se esperaba producir, de las máquinas que están trabajando',
  kpiScrap: 'Scrap', kpiOEE: 'OEE', kpiMaquinas: 'Máquinas trabajando', pzas: 'pzas', piezas: 'piezas', de: 'de',
  leyendaCon: 'Con piezas en las últimas 24 h', leyendaSin: 'Sin piezas en las últimas 24 h',
  fuera: 'Fuera del reporte por no estar trabajando:',
  ordenAria: 'Orden de la tabla', ordenCumplimiento: 'Por cumplimiento', ordenCumplimientoAyuda: 'De la de menor cumplimiento a la de mayor',
  ordenCambio: 'Contra la semana pasada', ordenCambioAyuda: 'De la que más bajó a la que más subió',
  hint: 'De peor a mejor · sólo las máquinas que están trabajando · clic en un renglón o en su barra para ver el detalle',
  medidaAria: 'Medida de la gráfica', medidaCumplimiento: 'Cumplimiento %', medidaPiezas: 'Piezas de la semana',
  colArticulo: 'Artículo', colMaquina: 'Máquina', colCumplimiento: 'Cumplimiento',
  colCambio1: 'Contra la', colCambio2: 'semana pasada', colProducidas1: 'Producidas', colProducidas2: 'en la semana',
  colEsperadas1: 'Esperadas', colEsperadas2: 'a esta hora', colEsperadas2Cerrada: 'en la semana',
  colProgramados1: 'Paros', colProgramados2: 'programados', colParo1: 'Tiempo de paro', colParo2: 'en la semana',
  colScrap1: 'Scrap', colScrap2: 'de la semana', colOEE1: 'OEE', colOEE2: 'de la semana',
  colDisponibilidad: 'Disponib.', colRendimiento: 'Rendim.', colCalidad: 'Calidad', colParos: 'Paros',
  colEstandar: 'Tiempo estándar', colEsperadasSemana: 'Esperadas de la semana', colFuera: 'Fuera de horario',
  colAcumulado: 'Acumulado de la orden', colUltima: 'Última pieza', colProduccion: 'Producción',
  total: 'Total · {n} máquinas trabajando', totalUna: 'Total · 1 máquina trabajando',
  sinCoincidencias: 'Ninguna máquina coincide con la búsqueda.',
  sinEstandar: 'Sin tiempo estándar', sinEstandarAyuda: 'La orden no tiene tiempo estándar por pieza.',
  sinTiempoEsperadoAyuda: 'En esta semana no hubo tiempo en que se esperara producir.',
  sinSemanaPasadaAyuda: 'La semana pasada no tuvo tiempo esperado de producción.',
  sinSemanaCompletaAyuda: 'La orden no trabajó la semana pasada completa.',
  semanaPasadaAyuda: 'La semana pasada completa: {p} · {a} piezas de {e} esperadas',
  chipArranco: 'empezó en la semana',
  arrancoAyuda: 'Esta orden empezó el {fecha}: lo esperado se cuenta desde el inicio de la semana.',
  puntos: 'pts', cerrar: 'Cerrar ✕', cargando: 'consultando…',
  estados: { 0: 'No iniciada', 1: 'Procesando', 2: 'Pausado', 3: 'Completado', 4: 'Iniciada parcialmente', 5: 'Finalizada' },
  detProducidas: 'Producidas en la semana', detEsperadas: 'Esperadas a esta hora', detEsperadasCerrada: 'Esperadas en la semana',
  detCumplimiento: 'Cumplimiento', detSemanaPasada: 'Semana pasada completa', detSemanaPasadaFechas: 'Semana pasada completa ({a} a {b})',
  detSemanaPasadaValor: '{p} · {a} piezas de {e} esperadas · cambio {c} puntos',
  detProgramados: 'Paros programados registrados', detProgramadosValor: '{a} h de {b} h laborables transcurridas',
  detParo: 'Tiempo de paro en la semana (sin los programados)', detParoValor: '{a} h · {p} del tiempo en que se esperaba producir',
  detParoAbierto: ' · hay un paro abierto desde el {fecha}',
  detScrap: 'Scrap en la semana', detOEE: 'OEE de la semana',
  detOEEValor: '{o} = disponibilidad {d} × rendimiento {r} × calidad {c}',
  detEstandarCiclo: 'Tiempo estándar · ciclo real de la semana', sinPiezasParaCiclo: 'sin piezas suficientes',
  cicloAyuda: 'Ciclo real: tiempo típico entre una pieza y la siguiente, sin contar pausas de diez minutos o más.',
  detEsperadasSemana: 'Esperadas de la semana completa', detAcumulado: 'Acumulado de la orden', detInicioReal: 'Inicio real',
  detUltima: 'Última pieza',
  tipLinea2: '{a} producidas de {e} esperadas · {p}', tipLinea2SinEsperadas: '{a} producidas', tipUltima: 'Última pieza: {f} ({h})',
  haceMinutos: 'hace minutos', haceHoras: 'hace {n} h', haceDias: 'hace {n} días', haceUnDia: 'hace 1 día',
  ariaCumplimiento: 'Cumplimiento semanal por máquina: producidas entre esperadas', ariaPiezas: 'Piezas producidas en la semana por máquina',
  sinHorario: 'La planta no tiene un horario configurado, así que no se puede calcular lo esperado. Revisa Recursos humanos › Horarios.',
  sinMaquinas: 'Ninguna máquina ha producido piezas en esta semana.',
  sinEstandarVarias: '{n} órdenes no tienen tiempo estándar por pieza: salen sin esperadas, cumplimiento ni OEE, y no entran a los porcentajes del total.',
  sinEstandarUna: '1 orden no tiene tiempo estándar por pieza: sale sin esperadas, cumplimiento ni OEE, y no entra a los porcentajes del total.',
  sinTiempoEsperadoVarias: '{n} órdenes tuvieron todo el tiempo transcurrido de la semana en paros programados: salen sin esperadas, cumplimiento ni OEE, y sus piezas no entran a los porcentajes del total.',
  sinTiempoEsperadoUna: '1 orden tuvo todo el tiempo transcurrido de la semana en paros programados: sale sin esperadas, cumplimiento ni OEE, y sus piezas no entran a los porcentajes del total.',
  topeDeOrdenes: 'Hay más órdenes en progreso de las que este reporte puede mostrar a la vez; se muestran las primeras.',
  error: 'No se pudo consultar el avance de la semana. Intenta de nuevo en un momento.',
  sinPermiso: 'No tienes permiso para ver este reporte.', reintentar: 'Reintentar',
  ayudaEsperadas: 'Esperadas a esta hora = (horas del horario de planta transcurridas en la semana − paros programados que la planta registró en esa máquina) entre el tiempo estándar por pieza. Un paro programado que no se registra no se descuenta; cualquier otro paro cuenta como tiempo de paro.',
  ayudaConTipos: 'Se descuentan como paros programados: {nombres}.',
  ayudaSinTipos: 'En esta planta no se descuentan paros programados.',
  ayudaProducidas: 'Producidas en la semana = piezas reportadas desde que arranca la semana de trabajo, incluidas las hechas fuera de horario.',
  ayudaSemana: 'La semana empieza el {dia} a las {hora}, cuando el horario de la planta reabre después de su descanso más largo, y dura siete días.',
  ayudaTrabajando: 'Sólo entran las máquinas que están trabajando: la que no ha hecho ni una pieza en la semana queda fuera de la tabla, de la gráfica y de los totales, y se nombra arriba.',
  ayudaCumplimiento: 'Cumplimiento = producidas entre esperadas. Contra la semana pasada = puntos de cumplimiento de diferencia entre lo que va de esta semana y la semana pasada completa, calculada con la misma regla. Una orden que empezó a media semana se marca en su renglón: lo esperado se cuenta desde el inicio de la semana, y no se compara contra la semana pasada si no la trabajó completa.',
  ayudaParo: 'Tiempo de paro = los demás paros que detienen la producción, contando sólo lo que cae dentro del horario de planta, y su % es sobre el tiempo en que sí se esperaba producir; un paro que sigue abierto cuenta hasta la hora de la consulta. Scrap = piezas de los reportes de scrap de la semana.',
  ayudaOEE: 'OEE = disponibilidad (tiempo esperado menos tiempo de paro, entre tiempo esperado) × rendimiento (piezas por su tiempo estándar entre el tiempo sin paro) × calidad (piezas sin scrap entre piezas).',
  ayudaUnaOrden: 'Cada máquina trabaja una sola orden, así que el OEE de la orden es el de su máquina.',
  ayuda24: '«Sin piezas en las últimas 24 h» compara la última pieza contra la hora de la consulta.',
  ayudaEstandar: 'El cumplimiento y el OEE dependen del tiempo estándar por pieza que tiene capturado cada orden. Si ese tiempo es mayor que el ciclo real de la máquina, el porcentaje sale más alto de lo que es; si es menor, más bajo. El detalle de cada máquina enseña su tiempo estándar junto a su ciclo real de la semana.',
  ayudaHorario: 'Las semanas pasadas se calculan con el horario que la planta tiene configurado hoy; si el horario cambia, cambia también desde dónde se cuenta la semana.',
  dias: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  diasCortos: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  meses: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
};

// El API puede contestar las llaves con minúscula o con mayúscula inicial: se aceptan las dos.
function val(o, campo) {
  if (o == null) return undefined;
  if (o[campo] !== undefined) return o[campo];
  return o[campo.charAt(0).toUpperCase() + campo.slice(1)];
}

function num(v) { return v == null ? null : Number(v); }
function dos(n) { return String(n).padStart(2, '0'); }

// "2026-09-13T21:00:00" -> partes numéricas, sin pasar por zonas horarias: la hora es la de la planta.
function partesFecha(texto) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(texto || ''));
  if (!m) return null;
  return { a: +m[1], m: +m[2], d: +m[3], h: +m[4], mi: +m[5], s: +(m[6] || 0) };
}
function aMilisegundos(p) { return Date.UTC(p.a, p.m - 1, p.d, p.h, p.mi, p.s); }
function diaSemana(p) { return new Date(Date.UTC(p.a, p.m - 1, p.d)).getUTCDay(); }

export default {
  props: ['active', 'esFavorito'],
  data() {
    return {
      T: TEXTOS,
      // Semanas, áreas y tipos de paro programado: se piden una vez, al montarse.
      alcance: null,
      cargandoAlcance: true,
      sinAlcance: false,
      puedeReintentar: false,
      loading: false,
      // Se pidió otra consulta mientras había una en vuelo: se lanza al terminar. Nunca hay dos a la vez.
      repetir: false,
      mensajeError: null,
      filtros: { referencia: null, idArea: null },
      respuesta: null,
      texto: '',
      todas: false,
      soloBajaron: false,
      orden: 'cumplimiento',
      medida: 'cumplimiento',
      sel: null,
      hot: null,
      acumulados: {}, // por orden: { acumulado, inicioReal }
      cargandoAcumulado: false,
      ciclos: {}, // por orden y semana: { ciclo } cuando ya contestó
      ultimaCarga: 0,
      reloj: null,
      tip: { visible: false, x: 0, y: 0, titulo: '', linea1: '', linea2: '', linea3: '' },
      // La gráfica se dibuja con el ancho REAL de su contenedor (una unidad del SVG = un pixel): el texto sale a su tamaño en cualquier ancho.
      anchoContenedor: 0,
      // Tabla y gráfica van lado a lado; sólo se apilan cuando el tablero mide menos que ANCHO_MINIMO_LADO_A_LADO.
      apilado: false,
      observador: null,
      G: { rh: 24, t: 6, r: 58, b: 24, l: 122 },
    };
  },
  computed: {
    semanas() {
      return (val(this.alcance, 'semanas') || []).map(s => {
        const ini = val(s, 'inicio'), fin = val(s, 'finLaborable') || val(s, 'fin');
        return { inicio: ini, texto: `${this.fechaSinDia(ini, true)} → ${this.fechaSinDia(fin, false)}${val(s, 'enCurso') ? ' ' + this.T.semanaEnCurso : ''}` };
      });
    },
    areas() {
      return (val(this.alcance, 'areas') || []).map(a => ({ idArea: val(a, 'idArea'), nombre: val(a, 'nombre') }));
    },
    enCurso() { return !!val(this.respuesta, 'enCurso'); },
    sinHorario() { return !!val(this.respuesta, 'sinHorario'); },
    topeDeOrdenes() { return !!val(this.respuesta, 'topeDeOrdenesAlcanzado'); },
    fueraDelReporte() { return val(this.respuesta, 'fueraDelReporte') || []; },
    renglones() {
      return (val(this.respuesta, 'renglones') || []).map(r => ({
        id: val(r, 'idTrabajoOrden'), clave: val(r, 'clave'), articulo: val(r, 'articulo'), maquina: val(r, 'maquina') || '',
        area: val(r, 'area') || '', estado: val(r, 'estadoProduccion'), estandar: num(val(r, 'tiempoEstandarSeg')),
        producidas: num(val(r, 'producidas')), esperadas: num(val(r, 'esperadas')), esperadasSemana: num(val(r, 'esperadasSemanaCompleta')),
        cumplimiento: num(val(r, 'cumplimiento')), producidasAnterior: num(val(r, 'producidasAnterior')),
        esperadasAnterior: num(val(r, 'esperadasAnterior')), cumplimientoAnterior: num(val(r, 'cumplimientoAnterior')),
        cambio: num(val(r, 'cambioPuntos')), segundosProgramados: num(val(r, 'segundosProgramados')),
        segundosParo: num(val(r, 'segundosParo')), porcentajeParo: num(val(r, 'porcentajeParo')), numeroParos: num(val(r, 'numeroParos')),
        paroAbiertoDesde: val(r, 'paroAbiertoDesde') || null, scrap: num(val(r, 'scrap')), disponibilidad: num(val(r, 'disponibilidad')),
        rendimiento: num(val(r, 'rendimiento')), calidad: num(val(r, 'calidad')), oee: num(val(r, 'oEE') !== undefined ? val(r, 'oEE') : val(r, 'oee')),
        fuera: num(val(r, 'piezasFueraDeHorario')), ultima: val(r, 'ultimaPieza') || null, sinPiezas24: !!val(r, 'sinPiezasEn24Horas'),
        sinEstandar: !!val(r, 'sinEstandar'), sinTiempoEsperado: !!val(r, 'sinTiempoEsperado'), inicioOrden: val(r, 'inicioOrden') || null,
        arrancoEnLaSemana: !!val(r, 'arrancoEnLaSemana'), sinSemanaAnteriorCompleta: !!val(r, 'sinSemanaAnteriorCompleta'),
      }));
    },
    resumen() {
      const r = val(this.respuesta, 'resumen') || {};
      return {
        producidas: num(val(r, 'producidas')), esperadas: num(val(r, 'esperadas')), cumplimiento: num(val(r, 'cumplimiento')),
        cambio: num(val(r, 'cambioPuntos')), segundosParo: num(val(r, 'segundosParo')), porcentajeParo: num(val(r, 'porcentajeParo')),
        segundosProgramados: num(val(r, 'segundosProgramados')), scrap: num(val(r, 'scrap')),
        oee: num(val(r, 'oEE') !== undefined ? val(r, 'oEE') : val(r, 'oee')), numeroParos: num(val(r, 'numeroParos')),
        esperadasSemana: num(val(r, 'esperadasSemanaCompleta')), fuera: num(val(r, 'piezasFueraDeHorario')),
        maquinasTrabajando: num(val(r, 'maquinasTrabajando')) || 0, maquinasTotales: num(val(r, 'maquinasTotales')) || 0,
        sinEstandar: num(val(r, 'sinEstandar')) || 0,
        sinTiempoEsperado: num(val(r, 'sinTiempoEsperado')) || 0,
      };
    },
    queMasBajaron() {
      return (val(this.respuesta, 'queMasBajaron') || []).map(b => ({
        id: val(b, 'idTrabajoOrden'), maquina: val(b, 'maquina'), anterior: num(val(b, 'cumplimientoAnterior')),
        actual: num(val(b, 'cumplimiento')), cambio: num(val(b, 'cambioPuntos')),
      }));
    },
    // Lo que se ve en la tabla y en la gráfica: el buscador y la tarjeta filtran aquí; el resumen no cambia con ellos.
    // Siempre de peor a mejor; los que no tienen valor van al final; a igualdad, por nombre de máquina.
    visibles() {
      const t = this.texto.trim().toLowerCase();
      const campo = this.orden == 'cambio' ? 'cambio' : 'cumplimiento';
      return this.renglones
        .filter(o => (!this.soloBajaron || (o.cambio != null && o.cambio < 0)) && (!t || `${o.clave} ${o.articulo} ${o.maquina}`.toLowerCase().indexOf(t) >= 0))
        .sort((a, b) => {
          if ((a[campo] == null) != (b[campo] == null)) return a[campo] == null ? 1 : -1;
          return (a[campo] - b[campo]) || String(a.maquina).localeCompare(String(b.maquina), 'es');
        });
    },
    seleccionada() { return this.renglones.find(o => o.id === this.sel) || null; },
    textoTotal() {
      const n = this.resumen.maquinasTrabajando;
      return n == 1 ? this.T.totalUna : this.T.total.replace('{n}', n);
    },
    textoSinEstandar() {
      const n = this.resumen.sinEstandar;
      return n == 1 ? this.T.sinEstandarUna : this.T.sinEstandarVarias.replace('{n}', n);
    },
    textoSinTiempoEsperado() {
      const n = this.resumen.sinTiempoEsperado;
      return n == 1 ? this.T.sinTiempoEsperadoUna : this.T.sinTiempoEsperadoVarias.replace('{n}', n);
    },
    nombreAlcance() {
      const a = this.areas.find(x => x.idArea === this.consultado.idArea);
      return a ? a.nombre : this.T.alcancePlanta.toLowerCase();
    },
    consultado() { return this.respuesta ? (this.respuesta.__consultado || {}) : {}; },
    subtitulo() {
      if (!this.respuesta) return null;
      const H = this.hrs(val(this.respuesta, 'segundosLaborablesSemana'));
      const base = (this.sinHorario ? this.T.subSinHorario : (this.enCurso ? this.T.subEnCurso : this.T.subCerrada))
        .replace('{a}', this.fechaDia(val(this.respuesta, 'inicio'), true, true))
        .replace('{b}', this.fechaDia(val(this.respuesta, 'finLaborable') || val(this.respuesta, 'fin'), true, true))
        .replace('{H}', H).replace('{alcance}', this.nombreAlcance);
      return base.replace('{h}', this.hrs(val(this.respuesta, 'segundosLaborablesTranscurridos'))).replace('{corte}', this.fechaCorta(val(this.respuesta, 'corte')));
    },
    // La ayuda se arma con lo que contestó el API: el inicio de semana se calcula, los tipos programados van por su nombre
    // y la frase de «una sola orden por máquina» sólo se pinta si es cierta en esta respuesta.
    ayuda() {
      const tipos = val(this.respuesta, 'tiposProgramados') || [];
      const ini = partesFecha(val(this.respuesta, 'inicio'));
      const lineas = [
        this.T.ayudaEsperadas + ' ' + (tipos.length ? this.T.ayudaConTipos.replace('{nombres}', tipos.join(', ')) : this.T.ayudaSinTipos),
        this.T.ayudaProducidas + (ini ? ' ' + this.T.ayudaSemana.replace('{dia}', this.T.dias[diaSemana(ini)]).replace('{hora}', `${dos(ini.h)}:${dos(ini.mi)}`) : '') + ' ' + this.T.ayudaTrabajando,
        this.T.ayudaCumplimiento,
        this.T.ayudaParo,
        this.T.ayudaOEE + (val(this.respuesta, 'unaOrdenPorMaquina') ? ' ' + this.T.ayudaUnaOrden : '') + (this.enCurso ? ' ' + this.T.ayuda24 : ''),
        this.T.ayudaEstandar,
        this.T.ayudaHorario,
      ];
      return lineas;
    },
    textoSemanaPasada() {
      const ini = val(this.respuesta, 'inicioAnterior');
      const i = this.semanas.findIndex(s => s.inicio == ini);
      if (i < 0) return this.T.detSemanaPasada;
      const s = (val(this.alcance, 'semanas') || [])[i];
      return this.T.detSemanaPasadaFechas.replace('{a}', this.fechaDia(val(s, 'inicio'), true)).replace('{b}', this.fechaDia(val(s, 'finLaborable') || val(s, 'fin'), false));
    },
    detalleSemanaPasada() {
      const o = this.seleccionada;
      if (o.cumplimientoAnterior == null || o.cambio == null) return '— · ' + (o.sinSemanaAnteriorCompleta ? this.T.sinSemanaCompletaAyuda : this.T.sinSemanaPasadaAyuda);
      return this.T.detSemanaPasadaValor.replace('{p}', this.pctf(o.cumplimientoAnterior)).replace('{a}', this.fmt(o.producidasAnterior, 0))
        .replace('{e}', this.fmt(o.esperadasAnterior, 0)).replace('{c}', (o.cambio < 0 ? '▼ ' : '▲ ') + this.pts(Math.abs(o.cambio)));
    },
    detalleProgramados() {
      return this.T.detProgramadosValor.replace('{a}', this.hrs(this.seleccionada.segundosProgramados)).replace('{b}', this.hrs(val(this.respuesta, 'segundosLaborablesTranscurridos')));
    },
    detalleParo() {
      const o = this.seleccionada;
      return this.T.detParoValor.replace('{a}', this.hrs(o.segundosParo)).replace('{p}', this.pctf(o.porcentajeParo))
        + (o.paroAbiertoDesde && this.enCurso ? this.T.detParoAbierto.replace('{fecha}', this.fechaCorta(o.paroAbiertoDesde)) : '');
    },
    detalleOEE() {
      const o = this.seleccionada;
      return this.T.detOEEValor.replace('{o}', this.pctf(o.oee)).replace('{d}', this.pctf(o.disponibilidad)).replace('{r}', this.pctf(o.rendimiento)).replace('{c}', this.pctf(o.calidad));
    },
    detalleCiclo() {
      const o = this.seleccionada;
      const c = this.ciclos[this.llaveCiclo(o.id)];
      const ciclo = !c ? this.T.cargando : (c.ciclo == null ? this.T.sinPiezasParaCiclo : this.fmt(Math.round(c.ciclo), 0) + ' s');
      return (o.sinEstandar ? this.T.sinEstandar : this.fmt(o.estandar, 0) + ' s') + ' · ' + ciclo;
    },
    // Geometría de la gráfica (sólo presentación)
    altoGrafica() { return this.G.t + this.G.b + Math.max(1, this.visibles.length) * this.G.rh; },
    anchoSvg() { return Math.max(ANCHO_MINIMO_GRAFICA, this.anchoContenedor); },
    anchoUtil() { return this.anchoSvg - this.G.l - this.G.r; },
    topeGrafica() {
      if (this.medida == 'cumplimiento') return Math.ceil(Math.max(1, ...this.visibles.map(o => o.cumplimiento || 0)) * 4) / 4;
      return Math.ceil(Math.max(1, ...this.visibles.map(o => o.producidas || 0)) / 1000) * 1000;
    },
  },
  watch: {
    // Al volver a estar activo, si el dato tiene más de un minuto se refresca en ese momento; al dejar de estarlo no se pide nada.
    active(activo) {
      if (activo && this.respuesta && Date.now() - this.ultimaCarga >= REFRESCO_MS) this.refrescaSiToca();
    },
  },
  mounted() {
    // El tablero cambia de ancho con la ventana, con el menú lateral y con la pantalla completa: se vuelve a medir y la gráfica se repinta.
    if (typeof ResizeObserver !== 'undefined' && this.$refs.board) {
      this.observador = new ResizeObserver(() => this.medir());
      this.observador.observe(this.$refs.board);
    } else window.addEventListener('resize', this.medir);
    // Un solo reloj para el refresco; se limpia al destruirse el componente.
    this.reloj = setInterval(() => this.refrescaSiToca(), REFRESCO_MS);
    this.getAlcance();
  },
  updated() {
    // La gráfica aparece hasta que hay respuesta: se mide en cuanto existe. Sólo cambia el estado si cambió la medida.
    this.medir();
  },
  beforeDestroy() {
    if (this.observador) this.observador.disconnect();
    else window.removeEventListener('resize', this.medir);
    if (this.reloj) clearInterval(this.reloj);
  },
  methods: {
    // ===== Llamadas al API =====
    getAlcance() {
      this.cargandoAlcance = true;
      this.sinAlcance = false;
      this.mensajeError = null;
      axios
        .get("/OTEnProgresoSemanal/Alcance")
        .then(response => {
          this.alcance = response.data || {};
          this.filtros.referencia = this.semanas.length ? this.semanas[0].inicio : null;
          this.cargandoAlcance = false;
          this.cargar(true);
        })
        .catch(error => {
          this.cargandoAlcance = false;
          this.sinAlcance = true;
          this.atiendeError(error);
        });
    },
    buscar() {
      if (this.cargandoAlcance || this.sinAlcance) return;
      // Si hay una consulta en vuelo, su respuesta se ignora y al terminar se lanza otra con los filtros que queden: nunca hay dos a la vez.
      if (this.loading) { this.repetir = true; return; }
      this.cargar(true);
    },
    cargar(delUsuario) {
      // Una sola petición de la semana en vuelo a la vez.
      if (this.loading) return;
      this.loading = true;
      this.mensajeError = null;
      const consultado = { referencia: this.filtros.referencia, idArea: this.filtros.idArea };
      axios
        .post("/OTEnProgresoSemanal/Semana", { Referencia: this.referenciaParaElApi(consultado.referencia), IdsArea: consultado.idArea == null ? [] : [consultado.idArea] })
        .then(response => {
          // El usuario cambió la semana o el alcance mientras tanto: esta respuesta ya no es la que pidió.
          if (this.repetir) return;
          const data = response.data || {};
          data.__consultado = consultado;
          this.respuesta = data;
          this.ultimaCarga = Date.now();
          this.tip.visible = false;
          if (this.sel != null && !this.renglones.some(o => o.id === this.sel)) this.sel = null;
          // El acumulado sólo se vuelve a pedir cuando lo pide el usuario, nunca por el refresco automático.
          if (delUsuario && this.todas) this.pedirAcumulado(this.renglones.map(o => o.id));
          if (this.sel != null) this.pedirDetalle(this.sel);
        })
        .catch(error => {
          if (this.repetir) return;
          this.atiendeError(error);
        })
        .finally(() => {
          this.loading = false;
          if (this.repetir) { this.repetir = false; this.cargar(true); }
        });
    },
    // El error puede llegar SIN respuesta (sin red): no se supone error.response. Nunca se pinta el mensaje que mande el API.
    atiendeError(error) {
      const codigo = error && error.response ? error.response.status : null;
      console.log('Avance semanal: no se pudo consultar', codigo);
      this.mensajeError = codigo === 403 ? this.T.sinPermiso : this.T.error;
      this.puedeReintentar = codigo !== 403;
    },
    reintentar() {
      if (this.sinAlcance || !this.alcance) this.getAlcance();
      else this.buscar();
    },
    pedirAcumulado(ids) {
      const faltan = ids.filter(id => id != null);
      if (!faltan.length) return;
      this.cargandoAcumulado = true;
      const lotes = [];
      for (let i = 0; i < faltan.length; i += ORDENES_POR_LLAMADA_ACUMULADO) lotes.push(faltan.slice(i, i + ORDENES_POR_LLAMADA_ACUMULADO));
      // Los lotes van uno tras otro: es la única consulta que crece con la edad de la orden.
      lotes.reduce((cadena, lote) => cadena.then(() => axios.post("/OTEnProgresoSemanal/Acumulado", { IdsTrabajoOrden: lote }).then(response => {
        (response.data || []).forEach(a => {
          this.$set(this.acumulados, val(a, 'idTrabajoOrden'), { acumulado: num(val(a, 'acumuladoOrden')), inicioReal: val(a, 'inicioReal') || null });
        });
      })), Promise.resolve())
        .catch(error => { console.log('Avance semanal: no se pudo consultar el acumulado', error && error.response ? error.response.status : null); })
        .finally(() => { this.cargandoAcumulado = false; });
    },
    pedirDetalle(id) {
      const llave = this.llaveCiclo(id);
      const referencia = val(this.respuesta, 'inicio');
      axios
        .get("/OTEnProgresoSemanal/Detalle/" + id, { params: { referencia } })
        .then(response => { this.$set(this.ciclos, llave, { ciclo: num(val(response.data || {}, 'cicloRealSeg')) }); })
        .catch(error => {
          console.log('Avance semanal: no se pudo consultar el ciclo real', error && error.response ? error.response.status : null);
          this.$set(this.ciclos, llave, { ciclo: null });
        });
    },
    llaveCiclo(id) { return id + '|' + val(this.respuesta, 'inicio'); },
    // La semana en curso se pide sin referencia: si la pantalla se queda abierta y la semana cambia, el API contesta la nueva.
    referenciaParaElApi(inicio) {
      return this.semanas.length && this.semanas[0].inicio == inicio ? null : inicio;
    },

    // ===== Refresco =====
    // A la vista de verdad: la pestaña del navegador visible Y el elemento pintado (una pestaña interna inactiva no lo pinta).
    estaALaVista() {
      const el = this.$refs.board;
      return document.visibilityState == 'visible' && !!el && el.getClientRects().length > 0;
    },
    // El reloj sólo pide si el reporte está activo, la semana elegida es la que está en curso, no hay consulta en vuelo
    // y el tablero está realmente a la vista.
    refrescaSiToca() {
      if (!this.active || !this.respuesta || this.loading || this.cargandoAlcance) return;
      if (this.referenciaParaElApi(this.filtros.referencia) != null || !this.enCurso) return;
      if (!this.estaALaVista()) return;
      this.cargar();
    },

    // ===== Interacción =====
    alCambiarTodas() { if (this.todas) this.pedirAcumulado(this.renglones.map(o => o.id)); },
    alternarBajaron() { this.soloBajaron = !this.soloBajaron; },
    alternarDetalle(id) {
      this.sel = this.sel === id ? null : id;
      if (this.sel == null) return;
      this.pedirAcumulado([id]);
      this.pedirDetalle(id);
      // El detalle se pinta debajo de la tabla y de la gráfica: se acerca a la vista.
      this.$nextTick(() => { const el = this.$refs.detalle; if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); });
    },
    clicBarra(o) { this.tip.visible = false; this.alternarDetalle(o.id); },
    entraBarra(o, evento) {
      this.hot = o.id;
      this.tip.titulo = o.clave;
      this.tip.linea1 = o.maquina + (o.area ? ' · ' + o.area : '');
      this.tip.linea2 = o.esperadas == null
        ? this.T.tipLinea2SinEsperadas.replace('{a}', this.fmt(o.producidas, 0))
        : this.T.tipLinea2.replace('{a}', this.fmt(o.producidas, 0)).replace('{e}', this.fmt(o.esperadas, 0)).replace('{p}', this.pctf(o.cumplimiento));
      this.tip.linea3 = this.T.tipUltima.replace('{f}', this.fechaCorta(o.ultima)).replace('{h}', this.hace(o.ultima));
      this.tip.visible = true;
      this.mueveTip(evento);
    },
    mueveTip(evento) {
      this.tip.x = Math.min(evento.clientX + 12, window.innerWidth - 300);
      this.tip.y = evento.clientY + 14;
    },
    saleBarra() { this.hot = null; this.tip.visible = false; },
    pantallaCompleta() {
      const el = this.$refs.board;
      if (!el) return;
      if (!document.fullscreenElement) {
        if (el.requestFullscreen) el.requestFullscreen();
      } else if (document.exitFullscreen) document.exitFullscreen();
    },

    // ===== Gráfica =====
    medir() {
      const tablero = this.$refs.board, grafica = this.$refs.grafica;
      if (tablero && tablero.clientWidth > 0) {
        const apilado = tablero.clientWidth < ANCHO_MINIMO_LADO_A_LADO;
        if (apilado != this.apilado) this.apilado = apilado;
      }
      if (grafica && grafica.clientWidth > 0) {
        const estilo = window.getComputedStyle(grafica);
        const ancho = Math.floor(grafica.clientWidth - parseFloat(estilo.paddingLeft || 0) - parseFloat(estilo.paddingRight || 0));
        if (ancho > 0 && ancho != this.anchoContenedor) this.anchoContenedor = ancho;
      }
    },
    gx(fraccion) { return this.G.l + this.anchoUtil * fraccion; },
    gy(i) { return this.G.t + i * this.G.rh + 4; },
    anchoGrafica(o) {
      const v = this.medida == 'cumplimiento' ? (o.cumplimiento || 0) : (o.producidas || 0);
      return Math.max(0, Math.min(this.anchoUtil, this.anchoUtil * v / this.topeGrafica));
    },
    etiquetaEje(fraccion) {
      return this.medida == 'cumplimiento' ? Math.round(this.topeGrafica * fraccion * 100) + '%' : this.fmt(this.topeGrafica * fraccion, 0);
    },
    // La barra de la tabla se llena hasta su ancho y no se desborda; el número sí es el real.
    anchoBarra(v) { return Math.max(0, Math.min(100, (v || 0) * 100)) + '%'; },

    // ===== Textos de celdas =====
    textoCambio(c) { return c == null ? '—' : (c < 0 ? '▼ ' : '▲ ') + this.pts(Math.abs(c)) + ' ' + this.T.puntos; },
    tituloCambio(o) {
      if (o.cambio == null) return o.sinSemanaAnteriorCompleta ? this.T.sinSemanaCompletaAyuda : (o.sinEstandar ? this.T.sinEstandarAyuda : (o.sinTiempoEsperado ? this.T.sinTiempoEsperadoAyuda : this.T.sinSemanaPasadaAyuda));
      return this.T.semanaPasadaAyuda.replace('{p}', this.pctf(o.cumplimientoAnterior)).replace('{a}', this.fmt(o.producidasAnterior, 0)).replace('{e}', this.fmt(o.esperadasAnterior, 0));
    },
    tituloSinValor(o) { return o.sinEstandar ? this.T.sinEstandarAyuda : this.T.sinTiempoEsperadoAyuda; },
    tituloArranco(o) { return this.T.arrancoAyuda.replace('{fecha}', this.fechaCorta(o.inicioOrden)); },
    textoAcumulado(id) {
      const a = this.acumulados[id];
      return a ? this.fmt(a.acumulado, 0) : (this.cargandoAcumulado ? this.T.cargando : '—');
    },
    textoInicioReal(id) {
      const a = this.acumulados[id];
      return a && a.inicioReal ? this.fechaCorta(a.inicioReal) : (this.cargandoAcumulado ? this.T.cargando : '—');
    },
    textoEstado(e) { return this.T.estados[e] || ''; },
    // Cuánto hace de la última pieza, contra la hora de la consulta que contestó el API.
    hace(texto) {
      const u = partesFecha(texto), c = partesFecha(val(this.respuesta, 'corte'));
      if (!u || !c) return '';
      const horas = (aMilisegundos(c) - aMilisegundos(u)) / 3600000;
      if (horas < 1) return this.T.haceMinutos;
      if (horas < 24) return this.T.haceHoras.replace('{n}', Math.round(horas));
      const dias = Math.round(horas / 24);
      return dias == 1 ? this.T.haceUnDia : this.T.haceDias.replace('{n}', dias);
    },

    // ===== Fechas y formatos =====
    // 18-sep 13:54 (con el año sólo si no es el de la consulta)
    fechaCorta(texto) {
      const p = partesFecha(texto);
      if (!p) return '—';
      const c = partesFecha(val(this.respuesta, 'corte'));
      return `${dos(p.d)}-${this.T.meses[p.m - 1]}${c && c.a != p.a ? '-' + p.a : ''} ${dos(p.h)}:${dos(p.mi)}`;
    },
    // 13-sep 21:00 · 19-sep (para el selector de semana, que tiene que caber sin cortarse)
    fechaSinDia(texto, conHora) {
      const p = partesFecha(texto);
      return p ? `${dos(p.d)}-${this.T.meses[p.m - 1]}${conHora ? ` ${dos(p.h)}:${dos(p.mi)}` : ''}` : '';
    },
    // dom 13-sep 21:00 · domingo 13-sep 21:00
    fechaDia(texto, conHora, largo) {
      const p = partesFecha(texto);
      if (!p) return '';
      const dia = (largo ? this.T.dias : this.T.diasCortos)[diaSemana(p)];
      return `${dia} ${dos(p.d)}-${this.T.meses[p.m - 1]}${conHora ? ` ${dos(p.h)}:${dos(p.mi)}` : ''}`;
    },
    fmt(n, d) { return Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: d }); },
    // Segundos a horas con un decimal (cambio de unidad para pintar).
    hrs(segundos) { return (Math.round(Number(segundos || 0) / 360) / 10).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 }); },
    // Los porcentajes llegan como fracción y sin tope; lo que no se puede calcular llega nulo y se pinta «—».
    pctf(n) { return n == null ? '—' : (Number(n) * 100).toFixed(1) + ' %'; },
    pts(n) { return Number(n || 0).toFixed(1); },
  },
};
</script>

<style scoped>
/* Mismo lenguaje visual del Pareto de paros, autocontenido en el componente. */
.os-board {
  --ink: #283139; --muted: #6b7682; --muted-2: #657993; --line: #e6e9ed; --blue: #2563d6; --kpi: #2d84da;
  --bar: #2d84da; --bar-soft: #bcd5f1; --warn: #b26a00; --warn-bg: #fff4df; --crit: #b3261e; --ok: #1e7b3c; --ok-bg: #e6f4ea;
  position: relative; max-width: 1480px; margin: 0 auto; background: #fff; color: var(--ink);
  border-radius: 12px; box-shadow: 0 1px 4px rgba(20, 40, 80, .08); padding: 16px 18px 22px;
  font-family: Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 13px;
}
.os-board:fullscreen { max-width: none; width: 100%; height: 100%; overflow: auto; border-radius: 0; }
.os-title-row { position: relative; }
/* La estrella de favoritos va en el renglón del título, a la derecha y fuera del flujo, para no sumar altura. */
.os-fav { position: absolute; right: 0; top: 50%; transform: translateY(-50%); margin: 0 !important; }
.os-board:fullscreen .os-fav { display: none; }
.os-title { font-size: 22px; font-weight: 500; letter-spacing: .01em; text-align: center; margin: 4px 0 2px; }
.os-sub { text-align: center; color: var(--muted); font-size: 12.5px; margin-bottom: 14px; }

.os-toolbar { display: flex; align-items: flex-start; gap: 12px; flex-wrap: nowrap; padding-top: 8px; }
.os-filtros { display: flex; align-items: center; gap: 8px; flex: 1 1 0; min-width: 0; flex-wrap: nowrap; }
/* Los filtros van en UN renglón; sólo con el tablero angosto (lo mide el componente) se permite que bajen. */
.os-angosto .os-toolbar, .os-angosto .os-filtros { flex-wrap: wrap; }
.os-input {
  flex: 0 1 190px; min-width: 120px; height: 42px; border: 1px solid var(--line); border-radius: 10px; background: #fff;
  display: flex; align-items: center; padding: 0 12px; gap: 8px; color: var(--ink); font-size: 13px; margin: 0; position: relative;
}
.os-input:focus-within { border-color: var(--blue); }
.os-texto { flex: 1 1 120px; }
.os-texto .lbl { position: absolute; top: -7px; left: 10px; background: #fff; padding: 0 4px; font-size: 10.5px; color: var(--muted); }
.os-texto input { border: 0; outline: 0; background: transparent; font: inherit; color: inherit; width: 100%; min-width: 0; }
.os-sel { justify-content: space-between; }
.os-sel--semana { flex: 0 0 auto; min-width: 306px; }
.os-sel .k { font-weight: 600; color: var(--muted); white-space: nowrap; }
.os-sel select {
  border: 0; outline: 0; background: transparent; font: inherit; font-weight: 600; color: var(--muted);
  width: 100%; min-width: 0; text-align: right; -webkit-appearance: menulist; appearance: menulist;
}
.os-switch { flex: 0 0 auto; min-width: 0; cursor: pointer; user-select: none; font-weight: 600; color: var(--muted); }
.os-switch input { width: 16px; height: 16px; accent-color: var(--blue); }
.os-btn {
  height: 42px; border-radius: 10px; border: 0; padding: 0 14px; font: inherit; font-weight: 600; letter-spacing: .04em;
  text-transform: uppercase; font-size: 12.5px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  white-space: nowrap; flex: 0 0 auto;
}
.os-btn:disabled { opacity: .55; cursor: default; }
.os-btn-primary { background: var(--blue); color: #fff; }
.os-btn-outline { background: #fff; color: var(--blue); border: 1px solid var(--blue); }
.os-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }

.os-aviso {
  margin-top: 12px; padding: 10px 14px; border: 1px solid var(--line); border-radius: 10px; background: #fffaf0;
  color: var(--ink); font-size: 13px;
}
.os-aviso--error { background: #fdecec; color: #8a2b2b; border-color: #f3c9c9; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.os-reintentar {
  height: 32px; border-radius: 10px; border: 1px solid var(--blue); background: #fff; color: var(--blue); padding: 0 14px;
  font: inherit; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; font-size: 12px; cursor: pointer;
}

.os-toprow { display: flex; align-items: stretch; gap: 12px; flex-wrap: nowrap; margin-top: 16px; }
.os-topcard {
  display: flex; align-items: center; gap: 8px 22px; background: #fff; border: 1px solid var(--line); border-radius: 10px;
  box-shadow: 0 1px 3px rgba(20, 40, 80, .06); padding: 8px 16px; min-width: 0; flex: 1 1 0;
}
.os-topcard--click { cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.os-topcard--click:hover, .os-topcard--click:focus-visible, .os-topcard--click.is-on { border-color: var(--blue); box-shadow: 0 2px 8px rgba(37, 99, 214, .15); outline: none; }
.os-topcard--click.is-on { background: #f5f9ff; }
.os-topcard--kpi { justify-content: space-between; column-gap: 18px; }
.os-sec { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.os-sec-head { display: flex; align-items: baseline; gap: 10px; }
.os-sec-title { color: var(--ink); font-size: 11px; font-weight: 700; line-height: 1.1; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; }
.os-sec-title--normal { font-weight: 400; }
.os-card-hint { color: var(--blue); font-size: 11px; font-weight: 600; white-space: nowrap; margin-left: auto; }
.os-sil { display: flex; gap: 18px; flex-wrap: nowrap; overflow: hidden; justify-content: space-between; }
.os-s3 { display: flex; align-items: baseline; gap: 8px; }
.os-s3 .d { color: var(--crit); font-size: 18px; font-weight: 800; line-height: 1; white-space: nowrap; font-variant-numeric: tabular-nums; }
.os-s3 .d small { font-size: 11px; font-weight: 600; margin-left: 2px; }
.os-s3 .n { display: flex; flex-direction: column; gap: 2px; }
.os-s3 .nom { font-size: 12px; font-weight: 700; white-space: nowrap; }
.os-s3 .sub { color: var(--muted-2); font-size: 10px; font-weight: 600; white-space: nowrap; }
.os-big { font-size: 20px; font-weight: 800; color: var(--ink); line-height: 1; letter-spacing: -.02em; white-space: nowrap; font-variant-numeric: tabular-nums; }
.os-big small { font-size: 12px; font-weight: 600; color: var(--muted); letter-spacing: 0; margin-left: 3px; }
.os-pct { font-size: 17px; font-weight: 800; letter-spacing: -.02em; color: var(--kpi); font-variant-numeric: tabular-nums; cursor: help; }
.os-row-inline { display: flex; align-items: baseline; gap: 10px; }
/* Dentro de la plataforma el menú lateral quita ancho: en pantallas medianas las dos tarjetas se apilan para que ningún indicador se parta en dos renglones. */
@media (max-width: 1560px) { .os-toprow { flex-wrap: wrap; } .os-topcard { flex: 1 1 100%; } }

.os-legend { display: flex; gap: 18px; flex-wrap: wrap; align-items: center; margin: 14px 2px 4px; }
.os-lg { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--muted); }
.os-lg i { display: inline-block; width: 14px; height: 10px; border-radius: 2px; }
.os-lg .sw-bar { background: var(--bar); }
.os-lg .sw-soft { background: var(--bar-soft); }
.os-lg--fuera { margin-left: auto; display: inline; }
.os-lg--fuera b { color: var(--ink); font-weight: 700; }

.os-blk { border: 1px solid var(--line); border-radius: 12px; background: #fff; margin-top: 12px; overflow: hidden; }
.os-blk-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 14px; border-bottom: 1px solid var(--line); background: #fafbfd; }
.os-hint { color: var(--muted); font-size: 12px; }
.os-seg { display: inline-flex; background: #eef1f4; border-radius: 999px; padding: 3px; }
.os-seg--r { margin-left: auto; }
.os-seg button { border: 0; background: transparent; font: inherit; font-size: 12px; font-weight: 600; color: var(--muted); padding: 4px 12px; border-radius: 999px; cursor: pointer; }
.os-seg button[aria-pressed="true"] { background: #fff; color: var(--blue); box-shadow: 0 1px 2px rgba(20, 40, 80, .12); }
.os-blk-body { display: grid; grid-template-columns: minmax(560px, 5fr) minmax(280px, 2fr); }
.os-tbl-wrap { border-right: 1px solid var(--line); overflow-x: auto; min-width: 0; }
.os-chart-wrap { padding: 10px 14px 8px; min-width: 0; overflow: hidden; }
/* Tabla a la izquierda y gráfica a la derecha, las dos a la vista. Si la tabla no cabe se desplaza DENTRO de su contenedor; la página nunca.
   Sólo se apilan cuando el tablero es angosto (lo decide el componente midiendo el tablero, no la ventana). */
.os-blk-body.os-apilado { grid-template-columns: minmax(0, 1fr); }
.os-blk-body.os-apilado .os-tbl-wrap { border-right: 0; border-bottom: 1px solid var(--line); }
.os-board table { border-collapse: collapse; width: 100%; font-size: 12.5px; font-variant-numeric: tabular-nums; }
.os-board thead th {
  background: #fff; text-align: right; font-size: 11px; font-weight: 700; color: var(--muted); text-transform: uppercase;
  letter-spacing: .03em; padding: 8px 6px; border-bottom: 1px solid var(--line); white-space: normal; line-height: 1.25; vertical-align: bottom;
}
.os-board thead th.l, .os-board td.l { text-align: left; }
.os-board tbody td { padding: 6px 6px; text-align: right; border-bottom: 1px solid #f0f2f5; white-space: nowrap; }
.os-board tbody tr { cursor: pointer; }
.os-board tbody tr:hover, .os-board tbody tr.is-hot { background: #eef4fc; }
.os-board tbody tr.is-sel { background: #e3eefc; }
.os-board tbody tr:focus-visible { outline: 2px solid var(--blue); outline-offset: -2px; }
.os-board tbody tr.vacio { cursor: default; }
.os-board tbody tr.vacio td { text-align: center; color: var(--muted); padding: 14px; }
td.ot { font-weight: 700; }
.mut { color: var(--muted); font-weight: 400; }
td.baja, tfoot td.baja { color: var(--crit); font-weight: 600; }
td.sube, tfoot td.sube { color: var(--ok); font-weight: 600; }
.os-av { display: inline-flex; align-items: center; gap: 8px; justify-content: flex-end; }
.os-av .b { width: 46px; height: 8px; border-radius: 999px; background: #eef1f5; overflow: hidden; display: inline-block; }
.os-av .b i { display: block; height: 100%; border-radius: 999px; background: var(--bar); }
.os-chip { display: inline-flex; align-items: center; gap: 5px; height: 20px; padding: 0 8px; border-radius: 10px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.os-chip--ok { background: var(--ok-bg); color: var(--ok); }
.os-chip--warn { background: var(--warn-bg); color: var(--warn); }
td.ot .os-chip { margin-left: 6px; }
.os-board tfoot td { padding: 8px 6px; font-weight: 700; text-align: right; border-top: 1px solid var(--line); background: #fafbfd; white-space: nowrap; }
.os-board tfoot td.l { text-align: left; }
.os-board tfoot .norm { font-weight: 400; }
.os-blk-foot { padding: 8px 14px 10px; color: var(--muted); font-size: 11.5px; border-top: 1px solid var(--line); }
.os-blk-foot p { margin: 0 0 4px; line-height: 1.45; }

.os-chart-wrap svg { display: block; max-width: 100%; overflow: visible; }
.ax { stroke: var(--line); stroke-width: 1; }
.ax-lbl { fill: var(--muted); font-size: 11px; }
.y-lbl { fill: var(--ink); font-size: 11.5px; font-weight: 500; }
.bar-rect { fill: var(--bar); }
.bar-rect.soft { fill: var(--bar-soft); }
.bar-rect.is-hot { fill: var(--blue); }
.val-lbl { fill: var(--ink); font-size: 11.5px; font-weight: 600; }
.hit { fill: transparent; cursor: pointer; }
.os-tip {
  position: fixed; pointer-events: none; z-index: 300; background: var(--ink); color: #fff; border-radius: 8px;
  padding: 8px 10px; font-size: 12px; line-height: 1.35; box-shadow: 0 4px 14px rgba(0, 0, 0, .2); max-width: 280px;
}
.os-tip b { font-weight: 700; }
.os-tip .m { color: #c9d2dc; }

.os-det { margin-top: 12px; border: 1px solid var(--line); border-radius: 12px; overflow: hidden; background: #fff; }
.os-det-head { padding: 10px 14px; background: #fafbfd; border-bottom: 1px solid var(--line); display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; }
.os-det-head b { font-size: 14px; }
.os-cerrar { cursor: pointer; }
.os-det-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px 18px; padding: 12px 14px; }
.kv { display: flex; flex-direction: column; gap: 2px; }
.kv span { font-size: 10.5px; color: var(--muted); text-transform: uppercase; letter-spacing: .03em; font-weight: 700; }
.kv b { font-size: 13px; font-variant-numeric: tabular-nums; }
.os-det-nota { padding: 0 14px 10px; color: var(--muted); font-size: 11.5px; }
@media (prefers-reduced-motion: reduce) { .os-board * { transition: none !important; } }
</style>
